import { useAuth } from '../../hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import {
    CreditCard,
    CheckCircle,
    AlertTriangle,
    Calendar,
    ArrowRight,
    Loader2,
    Shield,
} from 'lucide-react';
import { formatCurrency } from '@shared/utils';

export function SaaSBillingPage() {
    const { user, tenant } = useAuth();

    // Fetch subscription data
    const { data: subscription, isLoading: isLoadingSub } = useQuery({
        queryKey: ['saas-subscription', user?.id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('subscriptions')
                .select(`
                    *,
                    plan:plan_id (*)
                `)
                .eq('user_id', user?.id)
                .maybeSingle();

            if (error) throw error;
            return data;
        },
        enabled: !!user?.id,
    });

    // Fetch invoices for the SaaS
    const { data: invoices, isLoading: isLoadingInvoices } = useQuery({
        queryKey: ['saas-invoices', user?.id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('invoices')
                .select('*')
                .eq('user_id', user?.id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            return data;
        },
        enabled: !!user?.id,
    });

    if (isLoadingSub || isLoadingInvoices) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Assinatura GoFit</h1>
                <p className="text-gray-500">Gerencie seu plano e faturamento do sistema</p>
            </div>

            {/* Current Plan Card */}
            <div className="card border-primary-100 bg-primary-50/30">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                            <Shield className="w-6 h-6 text-primary-600" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-primary-600 mb-1">PLANO ATUAL</p>
                            <h2 className="text-xl font-bold text-gray-900">
                                {subscription?.plan?.name || 'Personal Grátis'}
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                {subscription?.status === 'active'
                                    ? `Sua assinatura está ativa e renova em ${new Date(subscription.current_period_end).toLocaleDateString()}`
                                    : 'Você está no plano gratuito. Faça o upgrade para desbloquear mais recursos.'}
                            </p>
                        </div>
                    </div>
                    <div>
                        <button className="btn-primary w-full md:w-auto">
                            Alterar Plano
                        </button>
                    </div>
                </div>

                {subscription?.status === 'past_due' && (
                    <div className="mt-6 p-4 rounded-lg bg-red-50 border border-red-100 flex items-center gap-3 text-red-700">
                        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                        <p className="text-sm">
                            Sua última fatura está atrasada. Regularize para evitar a suspensão dos recursos premium.
                        </p>
                    </div>
                )}
            </div>

            {/* Features Preview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card p-4">
                    <div className="flex items-center gap-2 text-green-600 mb-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-semibold">Alunos Ilimitados</span>
                    </div>
                    <p className="text-xs text-gray-500">Gerencie quantos atletas desejar sem custos extras.</p>
                </div>
                <div className="card p-4">
                    <div className="flex items-center gap-2 text-green-600 mb-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-semibold">WhatsApp Business</span>
                    </div>
                    <p className="text-xs text-gray-500">Notificações automáticas ilimitadas inclusas no seu plano.</p>
                </div>
                <div className="card p-4">
                    <div className="flex items-center gap-2 text-green-600 mb-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-semibold">Suporte Prioritário</span>
                    </div>
                    <p className="text-xs text-gray-500">Atendimento humanizado em menos de 2 horas via chat.</p>
                </div>
            </div>

            {/* Invoices */}
            <div className="card">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Histórico de Faturas</h3>
                {invoices && invoices.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b text-sm text-gray-500">
                                    <th className="py-3 px-4 font-medium">Data</th>
                                    <th className="py-3 px-4 font-medium">Valor</th>
                                    <th className="py-3 px-4 font-medium">Status</th>
                                    <th className="py-3 px-4 font-medium text-right">Ação</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {invoices.map((invoice) => (
                                    <tr key={invoice.id} className="text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                        <td className="py-3 px-4">
                                            {new Date(invoice.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="py-3 px-4 font-medium">
                                            {formatCurrency(invoice.total_cents)}
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className={`badge ${invoice.status === 'paid' ? 'badge-success' : 'badge-warning'
                                                }`}>
                                                {invoice.status === 'paid' ? 'Pago' : 'Pendente'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <button className="text-primary-600 hover:underline">
                                                Ver PDF
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <Calendar className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                        <p className="text-gray-500">Nenhuma fatura encontrada.</p>
                    </div>
                )}
            </div>

            {/* Payment Method */}
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Método de Pagamento</h3>
                    <button className="text-sm text-primary-600 font-medium hover:underline">
                        Atualizar
                    </button>
                </div>
                <div className="flex items-center gap-4 p-4 border rounded-xl bg-gray-50">
                    <div className="w-12 h-12 rounded-lg bg-white border flex items-center justify-center">
                        <CreditCard className="w-6 h-6 text-gray-400" />
                    </div>
                    <div>
                        <p className="font-bold text-gray-900">Cartão de Crédito</p>
                        <p className="text-sm text-gray-500">Terminado em •••• 4242</p>
                    </div>
                    <div className="ml-auto">
                        <span className="badge badge-primary">Padrão</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
